import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  SlotInputNode,
  SlotValuationInputsResponse,
} from 'types/packages-api/slot-valuation-inputs';
import type { MetaByNode } from './tearsheet-sections';
import type { SlotDisplayPatch, SlotOverrideOperation } from '../types';
import { overrideFieldKeyFor } from './editability';
import { neighborBounds, toIsoDay } from '../schedule-dialog/utils';
import { MILESTONE_LABELS, MILESTONE_ORDER } from './utils';
import { VOLUME_OVERRIDE_FIELDS } from './constants';
import type { VolumeSourceSelection } from 'components/volume-source-picker';
import { volumeOverrides } from '../volume-source-utils';

/**
 * ── THE TEARSHEET'S EDIT STATE, IN ONE PLACE ─────────────────────────────────────────────
 *
 * "There is no `useTearsheetEdit` hook. `editing`, `commitEdit`, `revertEdit`,
 * `commitVolumeEdit` and `dateBounds` are still in TearsheetTableView."
 *
 * ⚠️ A MOVE, NOT A CHANGE. Every line below is the line that was in the component, in the
 * same order, with the same dependency arrays — an extraction that reorders hooks changes
 * when they run, and this one does not: the six pieces were already adjacent and already ran
 * in this order. The rendered HTML of eight payloads is identical before and after, and the
 * edit, revert and commit paths are covered by the existing specs, which are untouched.
 *
 * WHAT IT TAKES AND WHAT IT RETURNS is the whole interface: everything the block read from
 * the component is a parameter, and everything the component read from the block is
 * returned. Nothing reaches back out.
 *
 * @param {object} params - The response, the node metadata, the slot and the commit sink.
 * @return {object} The edit state and the five callbacks the view renders with.
 */
export function useTearsheetEdit({
  response,
  meta,
  slotId,
  onCommitOverride,
}: {
  response: SlotValuationInputsResponse;
  meta: MetaByNode;
  slotId?: string | null;
  onCommitOverride?: (operation: SlotOverrideOperation) => void;
}) {
  const [editing, setEditing] = useState<SlotInputNode | null>(null);
  // ── WHAT THE EDITOR CALLS THE ROW ───────────────────────────────────────────────────────
  //
  // Set in the SAME callback that sets `editing`, and read only while `editing` is truthy, so
  // the two cannot disagree: nothing opens the editor without passing through #beginEdit.
  // Null only before the first edit of a session.
  const [editingRowLabel, setEditingRowLabel] = useState<string | null>(null);

  // ── ⚠️ THE EDITOR DOES NOT OUTLIVE THE SLOT ───────────────────────────────────────────
  //
  // `editing` is plain component state and nothing cleared it when `slotId` changed. On a
  // cache MISS that never showed, by accident: the panel declines a payload belonging to
  // another slot, `data` goes undefined, and TearsheetTableView unmounts — taking this hook
  // with it. On a cache HIT there is no blank and no unmount. React Query already holds the
  // new slot's payload, `header.slotId` matches immediately, and the open editor simply
  // survives the change.
  //
  // ⚠️ AND IT WOULD THEN WRITE TO THE NEW SLOT. The modal renders `slotId={slotId}` — the
  // prop, now the slot the user moved TO — while its field and heading come from `editing`,
  // which is still the row they opened on the slot they left. Saving lands that field on the
  // wrong slot. Same family as the payload carry-over, and the payload guard cannot see it
  // because the payload is correct; it is the editor that is stale.
  useEffect(() => {
    setEditing(null);
    setEditingRowLabel(null);
  }, [slotId]);

  // THE WRITE, AND THE THREE THINGS IT HAS TO GET RIGHT
  //
  //   the KEY     `overrideFieldKeyFor`, never the node key and never the raw engine
  //               field. Five of the nine differ from their node key, and a wrong key is
  //               dropped in silence by both consumers - see that function.
  //   the SHAPE   `kind: 'slot'`, exactly as the AFE and volume modals in slot-view build
  //               it, so the operation is indistinguishable from a grid edit downstream.
  //   PENDING     nothing is persisted here. The operation joins the working log and the
  //               endpoint previews it; it reaches the calculator on the next run, which is
  //               what the affordance's own copy promises.
  const commitEdit = useCallback(
    (value: string, displayPatch?: SlotDisplayPatch) => {
      const node = editing;
      setEditing(null);
      const key = node ? overrideFieldKeyFor(meta[node.key]) : null;
      // Both guards are real: `slotId` is optional on this surface, and `key` is null for a
      // node the registry never declared overridable. Writing under a null key would put the
      // literal string "null" in the overrides hash.
      if (!node || !slotId || !key || !onCommitOverride) {
        return;
      }
      onCommitOverride({
        kind: 'slot',
        slotId,
        overrides: { [key]: value },
        // ── ⚠️ THE COLUMNS THAT RIDE WITH AN AFE ────────────────────────────────────────
        //
        // "After an AFE edit on the tearsheet, the grid shows the new AFE id with the old
        // AFE's well name. The commit omits the displayPatch the grid's own edit sends, and
        // the refetch is refetchType: 'none'. Send afeWellName / afeSourceHref with it."
        //
        // Display-only and not serialized — `paintNewestOperation` applies it after the
        // overrides and it never joins `overriddenFields`. Omitted entirely when the caller
        // has nothing to paint, which is every editor but the AFE picker: an empty object
        // would be a claim that the well name is now blank.
        ...(displayPatch ? { displayPatch } : {}),
      });
    },
    [editing, meta, slotId, onCommitOverride]
  );

  // ── REVERT ────────────────────────────────────────────────────────────────────────────
  //
  // Writes through the SAME handler an edit does — `commitSlotOverride` — so a revert is
  // indistinguishable downstream from any other operation: same log, same invalidations,
  // same Calculate. The only difference is the entry's shape.
  //
  // The field written is the ENGINE field name (`overrideFieldKeyFor`), not the node key, for
  // the same reason a set is: the resolver folds the log by engine field, and `afe_header_id`
  // is spelled `afeSourceId` there.
  const revertEdit = useCallback(
    (node: SlotInputNode) => {
      const key = overrideFieldKeyFor(meta[node.key]);
      if (!slotId || !key || !onCommitOverride) {
        return;
      }
      // The volume family is set as one operation and must be cleared as one; every other
      // node owns exactly the key it writes.
      const fields: string[] =
        key === 'volumeSourceId' ? [...VOLUME_OVERRIDE_FIELDS] : [key];
      onCommitOverride({ kind: 'unset', slotId, fields });
    },
    [meta, slotId, onCommitOverride]
  );

  // THE ONE WAY IN. Both pieces of "which row is being edited" are set here, together, so a
  // heading can never name a different row than the node being written.
  const beginEdit = useCallback((node: SlotInputNode, rowLabel: string) => {
    setEditing(node);
    setEditingRowLabel(rowLabel);
  }, []);

  // ── ONE ORDER RULE, EVERY EDIT SURFACE ────────────────────────────────────────────────
  //
  // The lifecycle bounds for the milestone being edited, from the SAME `neighborBounds()`
  // the schedule dialog's drag, CSV apply and Table-tab edit already clamp with. Imported
  // rather than reimplemented: a second copy of "permit <= spud <= completion <= first
  // production" would drift from OverrideWriteValidator the way the FE/BE pair already has.
  //
  // WHY THIS EXISTS. The tearsheet was a new edit surface that skipped the clamp, so a
  // picked day that broke order was only rejected at Calculate, by a message naming two
  // rules and neither date.
  //
  // INCLUSIVE, and nil-skipping. `neighborBounds` considers EVERY dated field on each side and
  // returns the tightest one's own day — so completion on a slot with no spud is bounded by
  // permit, and the boundary day itself stays legal, which is what the validator's
  // `predecessor > written` / `successor < written` accepts. It used to say "walks outward to
  // the nearest PRESENT neighbour"; that was true of the old implementation and is the reading
  // that produced the mislabelled clamp.
  //
  // Non-milestone nodes (the five AFE cost dates) have no ordering constraint and get no
  // bounds, which is why an absent milestone returns an empty object rather than a clamp.
  const dateBounds = useMemo((): {
    min?: string;
    max?: string;
    minLabel?: string;
    maxLabel?: string;
  } => {
    const milestone = editing ? meta[editing.key]?.milestone : undefined;
    if (!milestone || !response.dates) {
      return {};
    }
    const order = [...MILESTONE_ORDER];
    const field = milestone as (typeof MILESTONE_ORDER)[number];
    // WHICH NEIGHBOUR EACH BOUND CAME FROM, taken from neighborBounds itself.
    //
    // ⚠️ IT USED TO BE RE-DERIVED HERE, BY WALKING OUTWARD TO THE NEAREST DATED NEIGHBOUR —
    // and a comment claimed that meant "the name and the number cannot disagree". They can.
    // neighborBounds returns the TIGHTEST neighbour, not the nearest, and on a disordered row
    // those are different fields: with permit 2030 and spud 2020, editing completion, the
    // bound is permit's 2030 while the walk names spud. The picker then refused 2021 and
    // blamed a date nine years earlier than the one actually binding it.
    const { minMs, minField, maxMs, maxField } = neighborBounds(
      response.dates,
      field,
      order
    );
    return {
      min: Number.isFinite(minMs) ? toIsoDay(minMs) : undefined,
      max: Number.isFinite(maxMs) ? toIsoDay(maxMs) : undefined,
      minLabel: minField ? MILESTONE_LABELS[minField] : undefined,
      maxLabel: maxField ? MILESTONE_LABELS[maxField] : undefined,
    };
  }, [editing, meta, response.dates]);

  // The effective date the edited row DISPLAYS, for the picker's initial value. Same
  // milestone resolution the bounds use — see the note at `currentDate` below.
  const currentEditingDate = useMemo((): string | undefined => {
    const fallback =
      typeof editing?.values?.[0]?.value === 'string'
        ? (editing.values[0].value as string)
        : undefined;
    const milestone = editing ? meta[editing.key]?.milestone : undefined;
    if (!milestone || !response.dates) {
      return fallback;
    }
    const merged = (
      response.dates as unknown as Record<
        string,
        { value?: unknown } | undefined
      >
    )[milestone];
    return typeof merged?.value === 'string' ? merged.value : fallback;
  }, [editing, meta, response.dates]);

  // ── ONE ROW, FIVE KEYS, AND THAT IS THE USER'S OWN CHOICE ─────────────────────────────
  //
  // The volume modal returns a forecast AND its kind, href, source and DCA scenario, because
  // its own UI asks for them together — the type selector is on screen beside the list. So the
  // write carries all five, byte-for-byte as the properties grid writes them
  // (slot-view's volume modal save), and is the same commitSlotOverride, not a second path (AC 3).
  //
  // ONLY ONE OF THE FIVE MOVES A ROW HERE. slot_volume_source reads the blob key the overlayer
  // writes, so Volume Source updates. The other four have no node that reflects them:
  // volumeSourceHref is display-only, forecast_source and dca_scenario have no node at all,
  // and the `volume_source_type` NODE is a homonym reading the PIPELINE version (`eval_3_0`),
  // which this write does not touch. See ModelMap::OVERRIDE_EXEMPT_BY_DESIGN.
  //
  // ⚠️ A REVERT MUST CLEAR ALL FIVE, WHICH IS WHY THE LIST IS SHARED.
  // `revertEdit` used to send the node's single override key, so a reverted volume source put
  // `volume_source_id` back to the scenario default while `volume_source_type`,
  // `forecast_source`, `dca_scenario` and the href stayed overridden — a mixed state the next
  // Calculate would price.
  //
  // ⚠️ THE SET IS TYPED AGAINST THE REVERT'S LIST. The REVERT
  // reads VOLUME_OVERRIDE_FIELDS; the SET builds its `overrides` as an object literal, and
  // nothing made the two keep agreeing — a field added to one was not added to the other, and
  // the failure is the mixed state described above. The set is now a `Record` keyed by that
  // list (`volumeOverrides`, shared with the grid's save since INS-13809 hands the modal's
  // selection over whole), so a key the list names and the set omits is a compile error
  // rather than a revert that leaves one behind.
  //
  // It is the keys the SET writes, not SlotOverrideEngine::VOLUME_FIELDS: measured, that
  // constant omits `volume_source_href` (display-only, carried but not a volume field) and
  // includes `is_user_modified`, which this write never sets. Unsetting what was never set
  // would be a second wrong answer.
  const commitVolumeEdit = useCallback(
    (selection: VolumeSourceSelection) => {
      setEditing(null);
      if (!slotId || !onCommitOverride) {
        return;
      }
      onCommitOverride({
        kind: 'slot',
        slotId,
        overrides: volumeOverrides(selection),
      });
    },
    [slotId, onCommitOverride]
  );

  return {
    editing,
    editingRowLabel,
    setEditing,
    beginEdit,
    commitEdit,
    revertEdit,
    commitVolumeEdit,
    dateBounds,
    currentEditingDate,
  };
}
